import { useState } from 'react'
import { Check, X, Link2, TrendingUp, Calendar, AlertCircle, Clock, Repeat } from 'lucide-react'
import { getFieldDisplayName } from '../../utils/itemValidation'
import { similarityService } from '../../services/similarityService'

interface HierarchyItemCardProps {
 item: any
 hierarchyType: string
 onAccept: (item: any, hierarchyType: string) => void
 onDecline: (item: any, hierarchyType: string) => void
}

// Helper to format day abbreviations
const formatDays = (days: string[]): string => {
 const dayMap: Record<string, string> = {
 'mon': 'Mon', 'tue': 'Tue', 'wed': 'Wed', 'thu': 'Thu', 
 'fri': 'Fri', 'sat': 'Sat', 'sun': 'Sun'
 }
 return days.map(d => dayMap[d.toLowerCase()] || d).join(', ')
}

// Helper to format recurrence pattern
const formatRecurrence = (recurrence: any): string => {
 if (!recurrence) return ''
 const { type, interval, days_of_week, end_condition } = recurrence
 
 let pattern = ''
 if (type === 'daily') {
 pattern = interval === 1 ? 'Daily' : `Every ${interval} days`
 } else if (type === 'weekly') {
 if (days_of_week && days_of_week.length > 0) {
 pattern = interval === 1 
 ? `Weekly on ${formatDays(days_of_week)}`
 : `Every ${interval} weeks on ${formatDays(days_of_week)}`
 } else {
 pattern = interval === 1 ? 'Weekly' : `Every ${interval} weeks`
 }
 } else if (type === 'monthly') {
 pattern = interval === 1 ? 'Monthly' : `Every ${interval} months`
 } else {
 pattern = type || 'Recurring'
 }
 
 if (end_condition === 'until_done') {
 pattern += ' (ongoing)'
 }
 
 return pattern
}

// Helper to format time window
const formatTimeWindow = (window: string): string => {
 const windowMap: Record<string, string> = {
 'early_morning': '🌅 Early Morning',
 'morning': '☀️ Morning',
 'afternoon': '🌤️ Afternoon',
 'evening': '🌆 Evening',
 'night': '🌙 Night',
 'any': 'Any time'
 }
 return windowMap[window] || window
}

export function HierarchyItemCard({ item, hierarchyType, onAccept, onDecline }: HierarchyItemCardProps) {
 const [showDetails, setShowDetails] = useState(false)
 const [showSimilarItems, setShowSimilarItems] = useState(true)
 
 const details = item.details || {}
 const fields = item.fields || {}
 const title = item.title || details.clean_summary || details.action || 'Untitled Item'
 const description = details.description || details.reasoning || ''
 const timeline = details.timeline
 const priority = fields.priority || details.priority
 const existingSimilarItems = item.existing_similar_items || []
 
 // Scheduling intent fields (from planner)
 const duration = fields.duration_minutes
 const preferredDays = fields.preferred_days
 const preferredTimeWindow = fields.preferred_time_window
 const recurrence = fields.recurrence
 const scheduleCandidate = fields.schedule_candidate
 const hasSchedulingIntent = preferredDays || preferredTimeWindow || recurrence || duration
 
 const getStatusColor = (status: string) => {
 const colors: Record<string, string> = {
 'active': 'bg-tint-ok-fill text-ok',
 'pending': 'bg-tint-warn-fill text-warn ',
 'completed': 'bg-brand-subtle text-brand ',
 'in_progress': 'bg-tint-info-fill text-info ',
 'paused': 'bg-surface-alt text-primary/30 ',
 'resolved': 'bg-tint-ok-fill text-ok',
 'blocked': 'bg-tint-bad-fill text-bad '
 }
 return colors[status] || colors['active']
 }
 
 const getProgressColor = (progress: number) => {
 if (progress >= 75) return 'bg-ok'
 if (progress >= 50) return 'bg-warn'
 if (progress >= 25) return 'bg-warn'
 return 'bg-bad'
 }

 return (
 <div className="border border-soft rounded-lg p-4 bg-surface shadow-sm hover:shadow-md transition-shadow">
 {/* Main Content */}
 <div className="space-y-3">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <h3 className="font-semibold text-primary line-clamp-2">
 {title}
 </h3>
 {description && (
 <p className="text-sm text-secondary mt-1 line-clamp-2">
 {description}
 </p>
 )}
 
 {/* Item metadata */}
 <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
 {timeline && (
 <span className="flex items-center gap-1 text-muted">
 <Calendar className="w-3 h-3" />
 {timeline}
 </span>
 )}
 {priority && (
 <span className={`px-2 py-0.5 rounded capitalize ${
 priority === 'high' || priority === 'critical' || priority === 'urgent'
 ? 'bg-tint-bad-fill text-bad '
 : priority === 'medium'
 ? 'bg-tint-warn-fill text-warn '
 : 'bg-surface-alt text-secondary/30 '
 }`}>
 {priority} priority
 </span>
 )}
 {duration && (
 <span className="flex items-center gap-1 text-muted">
 <Clock className="w-3 h-3" />
 {duration} min
 </span>
 )}
 </div>
 
 {/* Scheduling Intent Section (from Planner) */}
 {hasSchedulingIntent && (
 <div className="mt-2 p-2 bg-brand-subtle rounded-lg border border-brand-subtle">
 <div className="flex items-center gap-1.5 text-xs font-medium text-brand mb-1.5">
 <Repeat className="w-3.5 h-3.5" />
 Scheduling Intent
 </div>
 <div className="space-y-1 text-xs">
 {recurrence && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">🔄 Recurrence:</span>
 <span>{formatRecurrence(recurrence)}</span>
 </div>
 )}
 {preferredDays && preferredDays.length > 0 && !recurrence && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">📅 Preferred Days:</span>
 <span>{formatDays(preferredDays)}</span>
 </div>
 )}
 {preferredTimeWindow && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">⏰ Time Window:</span>
 <span>{formatTimeWindow(preferredTimeWindow)}</span>
 </div>
 )}
 {scheduleCandidate && (
 <div className="text-ok font-medium">
 ✓ Ready for scheduling
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 
 {/* Action buttons */}
 <div className="flex gap-2 flex-shrink-0">
 <button
 onClick={() => onAccept(item, hierarchyType)}
 className="p-2 rounded-lg bg-ok hover:bg-ok text-on-brand transition-colors"
 title="Accept and create item"
 >
 <Check className="w-4 h-4" />
 </button>
 <button
 onClick={() => onDecline(item, hierarchyType)}
 className="p-2 rounded-lg bg-bad hover:bg-bad text-on-brand transition-colors"
 title="Decline"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 </div>
 
 {/* Existing Similar Items Section */}
 {existingSimilarItems.length > 0 && showSimilarItems && (
 <div className="mt-3 pt-3 border-t border-soft">
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-2">
 <Link2 className="w-4 h-4 text-brand" />
 <span className="text-sm font-medium text-secondary">
 Existing Similar Items
 </span>
 </div>
 <button
 onClick={() => setShowSimilarItems(false)}
 className="text-xs text-muted hover:text-secondary"
 >
 Hide
 </button>
 </div>
 
 <div className="space-y-2">
 {existingSimilarItems.map((similarItem: any, index: number) => {
 const icon = similarityService.getCategoryIcon(similarItem.similarity_category)
 const matchPercentage = Math.round(similarItem.similarity_score * 100)
 const categoryColor = similarityService.getCategoryColor(similarItem.similarity_category)
 
 return (
 <div
 key={index}
 className="bg-brand-subtle rounded-lg p-3 text-sm"
 >
 <div className="flex items-start justify-between gap-2 mb-2">
 <div className="flex items-center gap-2 flex-1 min-w-0">
 <span className="text-lg flex-shrink-0">{icon}</span>
 <div className="flex-1 min-w-0">
 <div className="font-medium text-primary truncate">
 {similarItem.title}
 </div>
 <div className={`text-xs ${categoryColor}`}>
 {matchPercentage}% match
 </div>
 </div>
 </div>
 </div>
 
 {/* Progress and Status */}
 <div className="flex items-center gap-3 mt-2">
 {typeof similarItem.progress === 'number' && (
 <div className="flex items-center gap-2 flex-1">
 <TrendingUp className="w-3 h-3 text-muted" />
 <div className="flex-1 bg-surface-alt rounded-full h-2">
 <div
 className={`h-2 rounded-full transition-all ${getProgressColor(similarItem.progress)}`}
 style={{ width: `${similarItem.progress}%` }}
 />
 </div>
 <span className="text-xs text-secondary">
 {similarItem.progress}%
 </span>
 </div>
 )}
 
 {similarItem.status && (
 <span className={`px-2 py-0.5 rounded text-xs capitalize ${getStatusColor(similarItem.status)}`}>
 {similarItem.status}
 </span>
 )}
 </div>
 
 {/* Due date if available */}
 {(similarItem.due_date || similarItem.target_date) && (
 <div className="flex items-center gap-1 mt-2 text-xs text-secondary">
 <Calendar className="w-3 h-3" />
 {similarItem.due_date || similarItem.target_date}
 </div>
 )}
 
 {/* Info message */}
 <div className="flex items-start gap-2 mt-2 p-2 bg-brand-subtle rounded text-xs text-brand">
 <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
 <div>
 <strong>Already working on this!</strong> You can still create a new item or update the existing one.
 </div>
 </div>
 </div>
 )
 })}
 </div>
 </div>
 )}
 
 {/* Toggle to show hidden similar items */}
 {existingSimilarItems.length > 0 && !showSimilarItems && (
 <button
 onClick={() => setShowSimilarItems(true)}
 className="text-sm text-brand hover:underline flex items-center gap-1"
 >
 <Link2 className="w-3 h-3" />
 Show {existingSimilarItems.length} similar existing item{existingSimilarItems.length !== 1 ? 's' : ''}
 </button>
 )}
 
 {/* Toggle details */}
 <button
 onClick={() => setShowDetails(!showDetails)}
 className="text-xs text-muted hover:text-secondary"
 >
 {showDetails ? 'Hide' : 'Show'} details
 </button>
 
 {/* Extended details */}
 {showDetails && (
 <div className="mt-2 pt-2 border-t border-soft space-y-2 text-sm">
 {/* Details from item.details */}
 {Object.entries(details).map(([key, value]) => {
 if (!value || key === 'clean_summary' || key === 'action' || key === 'description' || key === 'reasoning') {
 return null
 }
 
 const displayName = getFieldDisplayName(key)
 const displayValue = Array.isArray(value) ? value.join(', ') : String(value)
 
 return (
 <div key={key} className="flex gap-2">
 <span className="font-medium text-secondary min-w-[100px]">
 {displayName}:
 </span>
 <span className="text-primary">
 {displayValue}
 </span>
 </div>
 )
 })}
 
 {/* Fields from item.fields (scheduling intent, etc.) */}
 {Object.keys(fields).length > 0 && (
 <>
 <div className="text-xs font-semibold text-muted mt-3 pt-2 border-t border-soft">
 Planner Fields:
 </div>
 {Object.entries(fields).map(([key, value]) => {
 if (!value || key === 'action_items') return null
 
 const displayName = getFieldDisplayName(key)
 let displayValue: string
 
 if (typeof value === 'object' && !Array.isArray(value)) {
 // For objects like recurrence, stringify nicely
 displayValue = JSON.stringify(value, null, 0)
 } else if (Array.isArray(value)) {
 displayValue = value.join(', ')
 } else {
 displayValue = String(value)
 }
 
 return (
 <div key={`field-${key}`} className="flex gap-2">
 <span className="font-medium text-brand min-w-[100px]">
 {displayName}:
 </span>
 <span className="text-primary font-mono text-xs">
 {displayValue}
 </span>
 </div>
 )
 })}
 </>
 )}
 </div>
 )}
 </div>
 </div>
 )
}
